import { c as _c } from "react/compiler-runtime";
import { add, setMilliseconds, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import { useTheme } from 'styled-components';
import { OutpostEventListParams } from '../../../../../../../typings/outpost';
import { OutpostEventView } from '../../../../../../../typings/View.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { deepMergeFilters, extractFromArray, toArray } from '../../../../utils';
import { RelativeDate, resolveRelativeDate } from '../../../../utils/date';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Badge from '../../../common/base/Badge';
import Skeleton from '../../../common/base/Skeleton';
import { StatusDot } from '../../../common/base/StatusDot';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import EmptyState, { StyledEmptyStateWrapper } from '../../../common/EmptyState';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import { StyledCentered } from '../../../common/helpers/StyledCentered';
import { Div } from '../../../common/helpers/StyledUtils';
import { useOutpostMetricsQuery, OutpostMetricsQueryParams } from '../../../hooks/useOutpostMetricsQuery';
import { FilterFormValues, outpost_date_filter_component, outpost_event_list_filters as filter_components } from '../../../common/Filters/FilterComponents';
import Filters, { FiltersContext } from '../../../common/Filters/Filters';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TableProvider } from '../../../contexts/TableContext';
import { useIsScrolled } from '../../../hooks/useIsScrolled';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewTitle } from '../../../layouts/ViewLayout';
import { DateFilter } from '../../DashboardScene/Events/Histogram';
import SuggestedColumns from '../../DashboardScene/Requests/SuggestedColumns';
import { ViewsContext } from '../../DashboardScene/ViewsContext';
import EventSidebar from './EventSidebar';
import EventsList from './EventsList';
import Histogram from './Histogram';
export interface OutpostEventFilters {
  date?: {
    relative?: RelativeDate;
    min?: string;
    max?: string;
  };
  tenant_id?: string[];
  topic?: string[];
  next?: string;
  prev?: string;
}
interface OutpostEventQueryParams {
  date?: string | {
    min?: string | string[];
    max?: string | string[];
    relative?: string;
  };
  tenant_id?: string | string[];
  topic?: string | string[];
  next?: string | string[];
  prev?: string | string[];
  current_page?: string;
  selected_event_id?: string | string[];
  dynamic_columns?: string[] | null;
}
const DEFAULT_DATE = 'yesterday';
const PAGE_SIZE = 50;
const extractFiltersFromQuery = (query: OutpostEventQueryParams): OutpostEventFilters => {
  let date = extractFromArray(query.date);
  if (typeof date === 'string') {
    const {
      relative,
      min,
      max
    } = resolveRelativeDate(date);
    date = {
      relative,
      min: min?.toISOString(),
      max: max?.toISOString()
    };
  } else {
    const parsed_date = query.date as {
      max?: string | string[];
      min?: string | string[];
    };
    date = {
      max: extractFromArray(parsed_date?.max) as string,
      min: extractFromArray(parsed_date?.min) as string
    };
  }
  return {
    date,
    tenant_id: query.tenant_id ? toArray(query.tenant_id) : undefined,
    topic: query.topic ? toArray(query.topic) : undefined,
    next: extractFromArray(query.next) as string,
    prev: extractFromArray(query.prev) as string
  };
};
const extractFiltersFromView = (current_view: OutpostEventView): OutpostEventFilters => {
  const filters = current_view.filters;
  return {
    ...filters,
    date: {
      relative: extractFromArray(filters.time?.relative) as RelativeDate,
      max: extractFromArray(filters.time?.max) as string,
      min: extractFromArray(filters.time?.min) as string
    }
  };
};
const filters_initial_state = extractFiltersFromQuery({
  date: DEFAULT_DATE
});
const POLL_NEW_EVENTS_INTERVAL = 5000;
const EventsView: React.FC<{
  current_view?: OutpostEventView;
}> = t0 => {
  const $ = _c(220);
  const {
    current_view
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const theme = useTheme();
  const [ignore_current_view, setIgnoreCurrentView] = useState(false);
  const [poll_new_events_ready, setPollNewEventsReady] = useState(false);
  const current_page = query.current_page ? parseInt(query.current_page, 10) : 1;
  const {
    has_scrolled,
    scrolled_element_ref
  } = useIsScrolled();
  const {
    views,
    createView,
    updateView,
    deleteView,
    renameView,
    duplicateView
  } = useContext(ViewsContext);
  let t1;
  if ($[0] !== query.selected_event_id) {
    t1 = extractFromArray(query.selected_event_id);
    $[0] = query.selected_event_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected_event_id = t1 as string;
  let new_filters;
  if ($[2] !== query) {
    new_filters = extractFiltersFromQuery(query);
    if (!query.date) {
      let t2;
      let t3;
      let t4;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        const {
          min,
          max
        } = resolveRelativeDate(DEFAULT_DATE);
        t2 = DEFAULT_DATE;
        t3 = min?.toISOString();
        t4 = max?.toISOString();
        $[4] = t2;
        $[5] = t3;
        $[6] = t4;
      } else {
        t2 = $[4];
        t3 = $[5];
        t4 = $[6];
      }
      let t5;
      if ($[7] !== t3 || $[8] !== t4) {
        t5 = {
          relative: t2,
          min: t3,
          max: t4
        };
        $[7] = t3;
        $[8] = t4;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      new_filters.date = t5;
    }
    $[2] = query;
    $[3] = new_filters;
  } else {
    new_filters = $[3];
  }
  if (current_view && !ignore_current_view) {
    const should_use_query = Object.values(filter_components).some(v => query[v.filter_key] !== undefined);
    if (!should_use_query) {
      let t2;
      if ($[10] !== current_view) {
        t2 = extractFiltersFromView(current_view);
        $[10] = current_view;
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      const view_filters = t2;
      let t3;
      if ($[12] !== new_filters.next || $[13] !== new_filters.prev || $[14] !== view_filters) {
        t3 = {
          ...filters_initial_state,
          ...view_filters,
          next: new_filters.next,
          prev: new_filters.prev
        };
        $[12] = new_filters.next;
        $[13] = new_filters.prev;
        $[14] = view_filters;
        $[15] = t3;
      } else {
        t3 = $[15];
      }
      new_filters = t3;
    }
  }
  const filters = new_filters;
  let t2;
  bb0: {
    if ("dynamic_columns" in query) {
      t2 = query.dynamic_columns === null ? undefined : query.dynamic_columns;
      break bb0;
    }
    if (current_view && !ignore_current_view && current_view.dynamic_columns) {
      t2 = current_view.dynamic_columns;
      break bb0;
    }
    t2 = undefined;
  }
  const dynamic_columns = t2;
  let params;
  if ($[16] !== filters.date || $[17] !== filters.next || $[18] !== filters.prev || $[19] !== filters.tenant_id || $[20] !== filters.topic) {
    params = {
      limit: PAGE_SIZE
    };
    if (filters.date?.relative) {
      let t3;
      let t4;
      if ($[22] !== filters.date.relative) {
        const {
          min: min_0,
          max: max_0
        } = resolveRelativeDate(filters.date.relative);
        t3 = min_0.toISOString();
        t4 = max_0.toISOString();
        $[22] = filters.date.relative;
        $[23] = t3;
        $[24] = t4;
      } else {
        t3 = $[23];
        t4 = $[24];
      }
      let t5;
      if ($[25] !== t3 || $[26] !== t4) {
        t5 = {
          gte: t3,
          lte: t4
        };
        $[25] = t3;
        $[26] = t4;
        $[27] = t5;
      } else {
        t5 = $[27];
      }
      params.time = t5;
    } else {
      if (filters.date?.min || filters.date?.max) {
        let time;
        if ($[28] !== filters.date) {
          time = {};
          if (filters.date?.min) {
            time.gte = filters.date.min;
          }
          if (filters.date?.max) {
            time.lte = filters.date.max;
          }
          $[28] = filters.date;
          $[29] = time;
        } else {
          time = $[29];
        }
        params.time = time;
      }
    }
    if (filters.tenant_id?.length) {
      params.tenant_id = filters.tenant_id;
    }
    if (filters.topic?.length) {
      params.topic = filters.topic;
    }
    if (filters.next) {
      params.next = filters.next;
    }
    if (filters.prev) {
      params.prev = filters.prev;
    }
    $[16] = filters.date;
    $[17] = filters.next;
    $[18] = filters.prev;
    $[19] = filters.tenant_id;
    $[20] = filters.topic;
    $[21] = params;
  } else {
    params = $[21];
  }
  const api_filters = params;
  let t3;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = APIMethodKeys.outpost.tenants.list({
      limit: 1
    });
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== HookdeckAPI.outpost.tenants) {
    t4 = () => HookdeckAPI.outpost.tenants.list({
      limit: 1
    });
    $[31] = HookdeckAPI.outpost.tenants;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  const {
    data: tenants_response
  } = useSWR(t3, t4);
  const has_tenants = !tenants_response || tenants_response.count > 0;
  let t5;
  if ($[33] !== api_filters) {
    t5 = APIMethodKeys.outpost.events.list(api_filters);
    $[33] = api_filters;
    $[34] = t5;
  } else {
    t5 = $[34];
  }
  let t6;
  if ($[35] !== HookdeckAPI.outpost.events || $[36] !== api_filters) {
    t6 = () => HookdeckAPI.outpost.events.list(api_filters);
    $[35] = HookdeckAPI.outpost.events;
    $[36] = api_filters;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      revalidateOnFocus: false
    };
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  const {
    data: events_response,
    isLoading: is_loading,
    mutate: mutateEvents
  } = useSWR(t5, t6, t7);
  let t8;
  if ($[39] !== events_response?.models) {
    t8 = events_response?.models || [];
    $[39] = events_response?.models;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  const events = t8;
  filters.date?.relative;
  let t9;
  bb1: {
    const poll_after_timestamp = filters.date?.relative && !events_response?.pagination.prev ? events_response?.models[0]?.time : null;
    if (!poll_after_timestamp) {
      t9 = null;
      break bb1;
    }
    let t10;
    if ($[41] !== poll_after_timestamp) {
      t10 = new Date(new Date(poll_after_timestamp).getTime() + 1000).toISOString();
      $[41] = poll_after_timestamp;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    let t11;
    if ($[43] !== t10) {
      t11 = {
        gt: t10
      };
      $[43] = t10;
      $[44] = t11;
    } else {
      t11 = $[44];
    }
    let t12;
    if ($[45] !== api_filters || $[46] !== t11) {
      t12 = {
        ...api_filters,
        time: t11,
        limit: 10,
        next: undefined,
        prev: undefined
      };
      $[45] = api_filters;
      $[46] = t11;
      $[47] = t12;
    } else {
      t12 = $[47];
    }
    t9 = t12;
  }
  const poll_new_events_filters = t9;
  let t10;
  let t11;
  if ($[48] !== poll_new_events_filters) {
    t10 = () => {
      if (!poll_new_events_filters) {
        setPollNewEventsReady(false);
        return;
      }
      const timeout_id = window.setTimeout(() => {
        setPollNewEventsReady(true);
      }, POLL_NEW_EVENTS_INTERVAL);
      return () => window.clearTimeout(timeout_id);
    };
    t11 = [poll_new_events_filters];
    $[48] = poll_new_events_filters;
    $[49] = t10;
    $[50] = t11;
  } else {
    t10 = $[49];
    t11 = $[50];
  }
  useEffect(t10, t11);
  const should_poll_new_events = poll_new_events_filters && poll_new_events_ready;
  let t12;
  if ($[51] !== poll_new_events_filters || $[52] !== should_poll_new_events) {
    t12 = should_poll_new_events && APIMethodKeys.outpost.events.list(poll_new_events_filters);
    $[51] = poll_new_events_filters;
    $[52] = should_poll_new_events;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== HookdeckAPI.outpost.events || $[55] !== poll_new_events_filters) {
    t13 = () => HookdeckAPI.outpost.events.list(poll_new_events_filters);
    $[54] = HookdeckAPI.outpost.events;
    $[55] = poll_new_events_filters;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      refreshInterval: POLL_NEW_EVENTS_INTERVAL,
      revalidateOnFocus: true
    };
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  const {
    data: new_events_response
  } = useSWR(t12, t13, t14);
  const has_next_results = !!new_events_response && new_events_response.models.length > 0;
  let metrics_filters;
  let stats_end_date;
  let stats_start_date;
  let t15;
  let t16;
  let t17;
  if ($[58] !== filters.date || $[59] !== filters.tenant_id || $[60] !== filters.topic) {
    const now = new Date();
    const end_date = setMilliseconds(filters.date?.max ? new Date(filters.date.max) : add(startOfHour(now), {
      hours: 1
    }), 0);
    const start_date = setMilliseconds(filters.date?.min ? new Date(filters.date.min) : sub(add(startOfHour(now), {
      hours: 1
    }), {
      days: 1
    }), 0);
    [stats_start_date, stats_end_date] = [start_date, end_date];
    let t18;
    if ($[67] !== filters.tenant_id) {
      t18 = filters.tenant_id?.length ? {
        tenant_id: filters.tenant_id
      } : {};
      $[67] = filters.tenant_id;
      $[68] = t18;
    } else {
      t18 = $[68];
    }
    let t19;
    if ($[69] !== filters.topic) {
      t19 = filters.topic?.length ? {
        topic: filters.topic
      } : {};
      $[69] = filters.topic;
      $[70] = t19;
    } else {
      t19 = $[70];
    }
    let t20;
    if ($[71] !== t18 || $[72] !== t19) {
      t20 = {
        ...t18,
        ...t19
      };
      $[71] = t18;
      $[72] = t19;
      $[73] = t20;
    } else {
      t20 = $[73];
    }
    metrics_filters = t20;
    t17 = "events";
    t15 = stats_start_date.toISOString();
    t16 = stats_end_date.toISOString();
    $[58] = filters.date;
    $[59] = filters.tenant_id;
    $[60] = filters.topic;
    $[61] = metrics_filters;
    $[62] = stats_end_date;
    $[63] = stats_start_date;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
  } else {
    metrics_filters = $[61];
    stats_end_date = $[62];
    stats_start_date = $[63];
    t15 = $[64];
    t16 = $[65];
    t17 = $[66];
  }
  let t18;
  if ($[74] !== t15 || $[75] !== t16) {
    t18 = {
      start: t15,
      end: t16
    };
    $[74] = t15;
    $[75] = t16;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = ["count"];
    $[77] = t19;
  } else {
    t19 = $[77];
  }
  let t20;
  if ($[78] !== metrics_filters || $[79] !== t17 || $[80] !== t18) {
    t20 = {
      endpoint: t17,
      time: t18,
      measures: t19,
      filters: metrics_filters
    };
    $[78] = metrics_filters;
    $[79] = t17;
    $[80] = t18;
    $[81] = t20;
  } else {
    t20 = $[81];
  }
  const count_query = t20;
  const {
    raw_data: count_data
  } = useOutpostMetricsQuery(count_query);
  const events_count = count_data?.[0]?.["Events.count"] ? parseInt(count_data[0]["Events.count"], 10) : undefined;
  let t21;
  if ($[82] !== updateSearchQuery) {
    t21 = id => updateSearchQuery({
      selected_event_id: id || undefined
    });
    $[82] = updateSearchQuery;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  const onEventSelected = t21;
  let t22;
  if ($[84] !== events || $[85] !== onEventSelected || $[86] !== selected_event_id) {
    t22 = () => {
      if (!selected_event_id || !events.length) {
        return;
      }
      const current_index = events.findIndex(event => event.id === selected_event_id);
      if (current_index > 0) {
        onEventSelected(events[current_index - 1].id);
      }
    };
    $[84] = events;
    $[85] = onEventSelected;
    $[86] = selected_event_id;
    $[87] = t22;
  } else {
    t22 = $[87];
  }
  const onPrevEvent = t22;
  let t23;
  if ($[88] !== events || $[89] !== onEventSelected || $[90] !== selected_event_id) {
    t23 = () => {
      if (!selected_event_id || !events.length) {
        return;
      }
      const current_index_0 = events.findIndex(event_0 => event_0.id === selected_event_id);
      if (current_index_0 < events.length - 1) {
        onEventSelected(events[current_index_0 + 1].id);
      }
    };
    $[88] = events;
    $[89] = onEventSelected;
    $[90] = selected_event_id;
    $[91] = t23;
  } else {
    t23 = $[91];
  }
  const onNextEvent = t23;
  let t24;
  if ($[92] !== current_view || $[93] !== dynamic_columns || $[94] !== filters.date || $[95] !== selected_event_id || $[96] !== updateSearchQuery) {
    t24 = new_filters_0 => {
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      updateSearchQuery({
        ...new_filters_0,
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_event_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["next", "prev", "current_page"],
        replace: true
      });
    };
    $[92] = current_view;
    $[93] = dynamic_columns;
    $[94] = filters.date;
    $[95] = selected_event_id;
    $[96] = updateSearchQuery;
    $[97] = t24;
  } else {
    t24 = $[97];
  }
  const onFilterChanged = t24;
  let t25;
  if ($[98] !== filters || $[99] !== updateSearchQuery) {
    t25 = date => {
      if (!date.relative) {
        delete date.relative;
      }
      updateSearchQuery({
        ...filters,
        date: date.relative ? date.relative : date
      }, {
        remove_keys: ["next", "prev", "current_page"]
      });
    };
    $[98] = filters;
    $[99] = updateSearchQuery;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  const setDate = t25;
  let t26;
  if ($[101] !== createView || $[102] !== dynamic_columns || $[103] !== filters.date) {
    t26 = values => {
      let view_filters_0 = filter_components.reduce((object, t27) => {
        const {
          form_name,
          formatForDB,
          isActive
        } = t27;
        if (!isActive(values[form_name])) {
          return object;
        }
        const formatted = formatForDB(values[form_name]);
        if (!formatted) {
          return object;
        }
        return deepMergeFilters(object, formatted);
      }, {});
      if (filters.date && outpost_date_filter_component.isActive(filters.date)) {
        const formatted_date = outpost_date_filter_component.formatForDB(filters.date);
        if (formatted_date) {
          view_filters_0 = deepMergeFilters(view_filters_0, formatted_date);
        }
      }
      createView(view_filters_0, dynamic_columns || []);
    };
    $[101] = createView;
    $[102] = dynamic_columns;
    $[103] = filters.date;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  const handleCreateViewWithValues = t26;
  let t27;
  if ($[105] !== current_view || $[106] !== dynamic_columns || $[107] !== filters.date || $[108] !== updateView) {
    t27 = values_0 => {
      let view_filters_1 = filter_components.reduce((object_0, t28) => {
        const {
          form_name: form_name_0,
          formatForDB: formatForDB_0,
          isActive: isActive_0
        } = t28;
        if (!isActive_0(values_0[form_name_0])) {
          return object_0;
        }
        const formatted_0 = formatForDB_0(values_0[form_name_0]);
        if (!formatted_0) {
          return object_0;
        }
        return deepMergeFilters(object_0, formatted_0);
      }, {});
      if (filters.date && outpost_date_filter_component.isActive(filters.date)) {
        const formatted_date_0 = outpost_date_filter_component.formatForDB(filters.date);
        if (formatted_date_0) {
          view_filters_1 = deepMergeFilters(view_filters_1, formatted_date_0);
        }
      }
      if (current_view) {
        updateView(current_view.id, view_filters_1, dynamic_columns || []);
      }
    };
    $[105] = current_view;
    $[106] = dynamic_columns;
    $[107] = filters.date;
    $[108] = updateView;
    $[109] = t27;
  } else {
    t27 = $[109];
  }
  const handleUpdateViewWithValues = t27;
  let t28;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setIgnoreCurrentView(false);
    };
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== current_view) {
    t29 = [current_view];
    $[111] = current_view;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  useEffect(t28, t29);
  let t30;
  if ($[113] !== events || $[114] !== selected_event_id) {
    let t31;
    if ($[116] !== selected_event_id) {
      t31 = e => e.id === selected_event_id;
      $[116] = selected_event_id;
      $[117] = t31;
    } else {
      t31 = $[117];
    }
    t30 = events.find(t31);
    $[113] = events;
    $[114] = selected_event_id;
    $[115] = t30;
  } else {
    t30 = $[115];
  }
  const event_1 = t30;
  let t31;
  if ($[118] !== current_view || $[119] !== deleteView || $[120] !== duplicateView || $[121] !== dynamic_columns || $[122] !== filters.date || $[123] !== handleCreateViewWithValues || $[124] !== handleUpdateViewWithValues || $[125] !== renameView || $[126] !== views) {
    t31 = t32 => {
      const {
        values: values_1
      } = t32;
      const filters_active_count = filter_components.reduce((count, f) => {
        if (f.isActive(values_1[f.form_name])) {
          count++;
        }
        return count;
      }, 0);
      const has_active_filters = filters_active_count > 0 || filters.date && outpost_date_filter_component.isActive(filters.date) && filters.date.relative !== DEFAULT_DATE || dynamic_columns && dynamic_columns.length > 0;
      if (!current_view) {
        if (views && views?.count > 249) {
          return <Tooltip align="right" tooltip="Views limit reached"><Button disabled={true} outline={true} icon="save">Save view</Button></Tooltip>;
        }
        return <Button outline={true} disabled={!has_active_filters} onClick={() => handleCreateViewWithValues(values_1)} icon="save">Save view</Button>;
      }
      return <ButtonGroup small={true} more_options={[{
        label: "Save as new view",
        disabled: views && views.count > 249,
        tooltip: views && views.count > 249 ? "Views limit reached" : undefined,
        onClick: () => handleCreateViewWithValues(values_1)
      }, {
        label: "Rename view",
        onClick: () => renameView(current_view.id)
      }, {
        label: "Duplicate view",
        onClick: () => duplicateView(current_view.id)
      }, {
        label: "Delete view",
        danger: true,
        onClick: () => deleteView(current_view.id)
      }]}><Button outline={true} onClick={() => handleUpdateViewWithValues(values_1)} disabled={!has_active_filters} icon="save">Save view</Button></ButtonGroup>;
    };
    $[118] = current_view;
    $[119] = deleteView;
    $[120] = duplicateView;
    $[121] = dynamic_columns;
    $[122] = filters.date;
    $[123] = handleCreateViewWithValues;
    $[124] = handleUpdateViewWithValues;
    $[125] = renameView;
    $[126] = views;
    $[127] = t31;
  } else {
    t31 = $[127];
  }
  const SaveViewButton = t31;
  let t32;
  if ($[128] !== dynamic_columns) {
    t32 = {
      dynamic_columns
    };
    $[128] = dynamic_columns;
    $[129] = t32;
  } else {
    t32 = $[129];
  }
  let t33;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    $[130] = t33;
  } else {
    t33 = $[130];
  }
  const t34 = current_view?.name ? `${current_view.name} events` : "Events";
  let t35;
  if ($[131] !== t34) {
    t35 = <ViewTitle title={t34} tooltip="Events represent published messages in your Outpost project. Use filters to inspect events by tenant, topic, or time range." />;
    $[131] = t34;
    $[132] = t35;
  } else {
    t35 = $[132];
  }
  let t36;
  if ($[133] !== count_data || $[134] !== events_count) {
    t36 = count_data === undefined ? <Skeleton variant="square" h={{
      px: 20
    }} /> : events_count !== undefined && <Badge neutral={true}><NumberFormat renderText={_temp} displayType="text" value={events_count} thousandSeparator="," /></Badge>;
    $[133] = count_data;
    $[134] = events_count;
    $[135] = t36;
  } else {
    t36 = $[135];
  }
  let t37;
  if ($[136] !== t35 || $[137] !== t36) {
    t37 = <Div flex={t33}>{t35}{t36}</Div>;
    $[136] = t35;
    $[137] = t36;
    $[138] = t37;
  } else {
    t37 = $[138];
  }
  const t38 = is_loading ? "loading" : "reload";
  const t39 = is_loading || !filters.date?.relative;
  let t40;
  if ($[139] !== mutateEvents || $[140] !== updateSearchQuery) {
    t40 = () => {
      updateSearchQuery({}, {
        remove_keys: ["prev", "next", "selected_event_id", "current_page"]
      });
      mutateEvents();
    };
    $[139] = mutateEvents;
    $[140] = updateSearchQuery;
    $[141] = t40;
  } else {
    t40 = $[141];
  }
  let t41;
  if ($[142] !== has_next_results) {
    t41 = has_next_results && <StatusDot color="primary" m={{
      l: 2
    }} />;
    $[142] = has_next_results;
    $[143] = t41;
  } else {
    t41 = $[143];
  }
  let t42;
  if ($[144] !== t38 || $[145] !== t39 || $[146] !== t40 || $[147] !== t41) {
    t42 = <Button icon={t38} outline={true} disabled={t39} onClick={t40}>Refresh{t41}</Button>;
    $[144] = t38;
    $[145] = t39;
    $[146] = t40;
    $[147] = t41;
    $[148] = t42;
  } else {
    t42 = $[148];
  }
  let t43;
  if ($[149] !== SaveViewButton) {
    t43 = <FiltersContext.Consumer>{t44 => {
        const {
          values: values_2
        } = t44;
        return <SaveViewButton values={values_2} />;
      }}</FiltersContext.Consumer>;
    $[149] = SaveViewButton;
    $[150] = t43;
  } else {
    t43 = $[150];
  }
  let t44;
  if ($[151] !== t42 || $[152] !== t43) {
    t44 = <StyledViewNavActions>{t42}{t43}</StyledViewNavActions>;
    $[151] = t42;
    $[152] = t43;
    $[153] = t44;
  } else {
    t44 = $[153];
  }
  let t45;
  if ($[154] !== t37 || $[155] !== t44) {
    t45 = <StyledViewNavHeader>{t37}{t44}</StyledViewNavHeader>;
    $[154] = t37;
    $[155] = t44;
    $[156] = t45;
  } else {
    t45 = $[156];
  }
  let t46;
  let t47;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      align: "flex-start",
      gap: 3
    };
    t47 = {
      x: 6,
      y: 3
    };
    $[157] = t46;
    $[158] = t47;
  } else {
    t46 = $[157];
    t47 = $[158];
  }
  let t48;
  if ($[159] !== filters || $[160] !== setDate || $[161] !== stats_end_date || $[162] !== stats_start_date) {
    t48 = <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} />;
    $[159] = filters;
    $[160] = setDate;
    $[161] = stats_end_date;
    $[162] = stats_start_date;
    $[163] = t48;
  } else {
    t48 = $[163];
  }
  let t49;
  if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <Divider vertical={true} h={{
      px: 28
    }} />;
    $[164] = t49;
  } else {
    t49 = $[164];
  }
  let t50;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Div w={100}><Filters.Form /></Div>;
    $[165] = t50;
  } else {
    t50 = $[165];
  }
  let t51;
  if ($[166] !== t48) {
    t51 = <StyledViewNavSecondary p={0}><Div flex={t46} p={t47}>{t48}{t49}{t50}</Div></StyledViewNavSecondary>;
    $[166] = t48;
    $[167] = t51;
  } else {
    t51 = $[167];
  }
  let t52;
  if ($[168] !== t45 || $[169] !== t51) {
    t52 = <StyledViewNav>{t45}{t51}</StyledViewNav>;
    $[168] = t45;
    $[169] = t51;
    $[170] = t52;
  } else {
    t52 = $[170];
  }
  let t53;
  let t54;
  if ($[171] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      overflow: "auto",
      height: "calc(100vh - 106px)",
      position: "relative"
    };
    t54 = {
      direction: "column"
    };
    $[171] = t53;
    $[172] = t54;
  } else {
    t53 = $[171];
    t54 = $[172];
  }
  let t55;
  if ($[173] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = {
      x: 6,
      y: 3
    };
    $[173] = t55;
  } else {
    t55 = $[173];
  }
  const t56 = events[0]?.time;
  let t57;
  if ($[174] !== filters || $[175] !== metrics_filters || $[176] !== setDate || $[177] !== stats_end_date || $[178] !== stats_start_date || $[179] !== t56) {
    t57 = <Div p={t55}><Histogram filters={filters} start_date={stats_start_date} end_date={stats_end_date} setDate={setDate} refresh_key={t56} metrics_filters={metrics_filters} /></Div>;
    $[174] = filters;
    $[175] = metrics_filters;
    $[176] = setDate;
    $[177] = stats_end_date;
    $[178] = stats_start_date;
    $[179] = t56;
    $[180] = t57;
  } else {
    t57 = $[180];
  }
  let t58;
  if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Divider />;
    $[181] = t58;
  } else {
    t58 = $[181];
  }
  let t59;
  if ($[182] !== events) {
    t59 = events.length > 0 && <><Div flex={{
        align: "flex-start",
        gap: 3
      }} p={{
        x: 6,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 2
        }} h={{
          px: 28
        }} m={{
          r: 4.5
        }}><Icon icon="add_column_right" muted={true} /><Text semi={true} muted={true} text_wrap={false}>Suggested columns</Text></Div><Divider vertical={true} /><Div w={100}><SuggestedColumns entity="outpost_events" models={events} /></Div></Div><Divider /></>;
    $[182] = events;
    $[183] = t59;
  } else {
    t59 = $[183];
  }
  let t60;
  if ($[184] !== t57 || $[185] !== t59) {
    t60 = <StyledCentered>{t57}{t58}{t59}</StyledCentered>;
    $[184] = t57;
    $[185] = t59;
    $[186] = t60;
  } else {
    t60 = $[186];
  }
  let t61;
  if ($[187] !== current_page || $[188] !== events_response || $[189] !== has_tenants || $[190] !== is_loading || $[191] !== onEventSelected || $[192] !== selected_event_id || $[193] !== theme || $[194] !== updateSearchQuery) {
    t61 = !has_tenants && !is_loading ? <StyledEmptyStateWrapper><EmptyState title="Publish your first event" description="Publish an event to one of your tenants with at least 1 destination. Use filters to inspect events by tenant, topic, or time range." asset={`outpost-events-${theme.mode}.png`} asset_position="background" cta={{
        label: "Get started",
        icon: "grid_view",
        to: "/get-started"
      }} /></StyledEmptyStateWrapper> : <ErrorBoundary><EventsList events_response={events_response} loading={is_loading} selected_event_id={selected_event_id} onEventSelected={onEventSelected} current_page={current_page} onNextPage={events_response?.pagination?.next ? () => {
        updateSearchQuery({
          next: events_response?.pagination?.next || "",
          prev: undefined,
          current_page: String(current_page + 1)
        });
      } : undefined} onPreviousPage={current_page > 1 ? () => {
        updateSearchQuery({
          prev: events_response?.pagination?.prev || undefined,
          next: undefined,
          current_page: String(current_page - 1)
        });
      } : undefined} /></ErrorBoundary>;
    $[187] = current_page;
    $[188] = events_response;
    $[189] = has_tenants;
    $[190] = is_loading;
    $[191] = onEventSelected;
    $[192] = selected_event_id;
    $[193] = theme;
    $[194] = updateSearchQuery;
    $[195] = t61;
  } else {
    t61 = $[195];
  }
  let t62;
  if ($[196] !== scrolled_element_ref || $[197] !== t60 || $[198] !== t61) {
    t62 = <Div style={t53} flex={t54} ref={scrolled_element_ref}>{t60}{t61}</Div>;
    $[196] = scrolled_element_ref;
    $[197] = t60;
    $[198] = t61;
    $[199] = t62;
  } else {
    t62 = $[199];
  }
  let t63;
  if ($[200] !== t52 || $[201] !== t62) {
    t63 = <StyledViewContent>{t52}{t62}</StyledViewContent>;
    $[200] = t52;
    $[201] = t62;
    $[202] = t63;
  } else {
    t63 = $[202];
  }
  let t64;
  if ($[203] !== event_1 || $[204] !== onNextEvent || $[205] !== onPrevEvent || $[206] !== selected_event_id || $[207] !== updateSearchQuery) {
    t64 = selected_event_id && <EventSidebar event_id={selected_event_id} list_event={event_1} onPrev={onPrevEvent} onNext={onNextEvent} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_event_id"]
    })} />;
    $[203] = event_1;
    $[204] = onNextEvent;
    $[205] = onPrevEvent;
    $[206] = selected_event_id;
    $[207] = updateSearchQuery;
    $[208] = t64;
  } else {
    t64 = $[208];
  }
  let t65;
  if ($[209] !== t63 || $[210] !== t64) {
    t65 = <StyledViewLayout>{t63}{t64}</StyledViewLayout>;
    $[209] = t63;
    $[210] = t64;
    $[211] = t65;
  } else {
    t65 = $[211];
  }
  let t66;
  if ($[212] !== filters || $[213] !== onFilterChanged || $[214] !== t65) {
    t66 = <Filters.Provider route="events" filters={filters} components={filter_components} onFilterChanged={onFilterChanged}>{t65}</Filters.Provider>;
    $[212] = filters;
    $[213] = onFilterChanged;
    $[214] = t65;
    $[215] = t66;
  } else {
    t66 = $[215];
  }
  let t67;
  if ($[216] !== has_scrolled || $[217] !== t32 || $[218] !== t66) {
    t67 = <TableProvider initialConfig={t32} has_scrolled={has_scrolled}>{t66}</TableProvider>;
    $[216] = has_scrolled;
    $[217] = t32;
    $[218] = t66;
    $[219] = t67;
  } else {
    t67 = $[219];
  }
  return t67;
};
export default EventsView;
function _temp(v_0) {
  return v_0;
}